import { LabelEditorField, useAssignFieldsToFarm, useField, useSetLabels } from '@cibo/landmanager'
import { AssignAndCreateFarmField } from '@cibo/landmanager/src/components/LandManagementProvider/hooks/useAssignFarmDialog/AssignFarmForm'
import { AuthUserPermission, Can, usePermissions } from '@cibo/profile'
import { MapPinLaptop } from '@cibo/ui'
import { Box, Container, Divider, Grid, InputLabel, Stack, Typography } from '@mui/material'
import { Form, Formik, useField as useFormikField } from 'formik'
import { useSnackbar } from 'notistack'
import { path } from 'ramda'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { generatePath, useNavigate } from 'react-router-dom'
import { AwaitDetails } from '../../components'
import ROUTE, { LAND_MANAGER_ROUTES } from '../../navigation/types/routes'
import { Forbidden } from '../Forbidden'

export const EditLandDetails = () => {
  const location = useLocation()
  const navStateFieldIds: string[] | undefined = path(['state', 'fields'], location)

  if (!navStateFieldIds) {
    return <Forbidden />
  }
  return (
    <Formik
      initialValues={{ 'metadata.termsets[0]': 'none' }}
      validateOnMount
      validateOnChange
      onSubmit={() => console.log('hi')}
    >
      {() => (
        <Form style={{ flex: 1 }}>
          <EditLandDetailsContent fieldIds={navStateFieldIds} />
        </Form>
      )}
    </Formik>
  )
}

const EditLandDetailsContent = ({ fieldIds }: { fieldIds: string[] }) => {
  const { t } = useTranslation('impact/pages/EditLandDetails')
  const { can } = usePermissions()
  const field = useField(fieldIds[0])
  const navigate = useNavigate()
  const [labels, setLabels] = useState<string[]>([])
  const saveLabels = useSetLabels()
  const assignFieldsToFarm = useAssignFieldsToFarm()
  const [{ value }] = useFormikField('farm')
  const { enqueueSnackbar } = useSnackbar()

  const onClickFinish = (onSuccess?: () => void) => {
    let errorContext = ''
    const promises = []
    if (fieldIds && labels && labels.length > 0) {
      promises.push(
        saveLabels.mutateAsync({
          resourceIds: fieldIds,
          labels,
        })
      )
      errorContext = 'label'
    }
    if (!!value) {
      promises.push(
        assignFieldsToFarm.mutateAsync({
          termsetId: value.id,
          fieldIds: fieldIds,
        })
      )
      const newErrorContext = errorContext === 'label' ? 'farmOrLabel' : 'farm'
      errorContext = newErrorContext
    }
    Promise.allSettled(promises)
      .then(() => (onSuccess ? onSuccess() : navigate(LAND_MANAGER_ROUTES.ROOT)))
      .catch(e => {
        enqueueSnackbar(t('couldntSaveMeta', { count: labels.length, context: errorContext }), {
          variant: 'error',
        })
        throw e
      })
  }

  return (
    <Container>
      <Stack spacing={4} divider={<Divider />}>
        <Box>
          <Grid container spacing={5}>
            <Grid container item xs={12} md={8}>
              <Grid item xs={12} p={2}>
                <Typography variant="h4" color="text.secondary">
                  {t('editDetailsTitle')}
                </Typography>
                <Stack spacing={2} alignItems="flex-start">
                  <Typography variant="body1">
                    {t('editDetailsSubhead', {
                      context: can({
                        useAny: [
                          AuthUserPermission.FARMS_MANAGE,
                          AuthUserPermission.FARMS_ASSIGN_OWNER,
                        ],
                      })
                        ? 'editFarm'
                        : '',
                    })}
                  </Typography>
                  <Typography variant="caption">
                    {t('editingDetails', {
                      count: fieldIds?.length,
                    })}
                  </Typography>

                  <LabelEditorField onChange={labels => setLabels(labels || [])} />

                  <Can useAny={[AuthUserPermission.FARMS_MANAGE]}>
                    <Stack
                      sx={{
                        width: '100%',
                      }}
                    >
                      <InputLabel shrink htmlFor="farm">
                        {t('farm')}
                      </InputLabel>
                      <AssignAndCreateFarmField
                        id="farm"
                        name="farm"
                        ownerId={field.data?.ownerId}
                      />
                    </Stack>
                  </Can>
                </Stack>
              </Grid>
            </Grid>
            <Grid container item xs={4} md={4}>
              <MapPinLaptop />
            </Grid>
          </Grid>
        </Box>
        <Box alignSelf="center" maxWidth={700}>
          <AwaitDetails
            fieldIds={fieldIds}
            onCompleteProfile={fieldIds => {
              onClickFinish(() => {
                if (fieldIds.length === 1) {
                  navigate(
                    generatePath(ROUTE.PROFILE_FIELD, {
                      fieldId: fieldIds[0],
                    })
                  )
                } else {
                  navigate(ROUTE.PROFILE_FIELDS, {
                    state: {
                      profileFieldIds: fieldIds,
                    },
                  })
                }
              })
            }}
            onCreateEnrollment={() => {
              navigate(LAND_MANAGER_ROUTES.ROOT)
            }}
            onSkipDetails={() => {
              onClickFinish()
            }}
          />
        </Box>
      </Stack>
    </Container>
  )
}
